import {
  Box,
  Button,
  capitalize,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { Else, If, Then, Unless, When } from 'react-if'
import { useParams } from 'react-router-dom'

import {
  FieldArray,
  Icon,
  useConfirm,
  VailAccordion,
  VailNumberInput,
  VailSelect,
  VailSwitch,
  VailTextField,
} from '@/components'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useListHistory, useOrg } from '@/hooks'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import { useListCustomComponent } from '@/screens/ComponentLibrary/hooks'
import { camelize, safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType } from '@/utils/api'

import { defaultSemanticSearch, useSemanticSearchTab } from '../hooks'

export const Settings = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()

  const org = useOrg(orgID)
  const {
    form,
    onSubmit,
    current,
    buttonsFieldArray,
    buttonsQuestionsArray,
    openAiAdverseEventTurboPrompt,
  } = useSemanticSearchTab(orgID, componentID)
  const [tempDefaultQuestion, setDefaultTempValue] = useState<string[]>([])
  const recipes = useListCustomComponent(
    orgID,
    {
      type: {
        eq: ComponentType.recipe,
      },
    },
    ['recipes']
  )
  const adverseEventCheckHistory = useListHistory(
    [
      'adverse event',
      'history',
      form.watch(
        'settings.largeLanguageModelSettings.adverseEventDetectedStepRecipe'
      ),
    ],
    'customComponent',
    {
      id: form.watch(
        'settings.largeLanguageModelSettings.adverseEventDetectedStepRecipe'
      ),
    }
  )
  const confirm = useConfirm()
  const withCustomPrompt = form.watch(
    'settings.largeLanguageModelSettings.customOpenAiAdverseEventTurboPrompt'
  )
  return (
    <FormProvider {...form}>
      <Grid
        container
        columnSpacing={2}
        component="form"
        height="100%"
        rowSpacing={1.5}
        onSubmit={onSubmit}
      >
        <Grid display="flex" justifyContent="flex-end" size={12}>
          <Button
            id="submit-button"
            sx={{ mb: 2 }}
            type="submit"
            variant="contained"
          >
            Save
          </Button>
        </Grid>
        <Grid size={12}>
          <VailAccordion
            defaultExpanded
            useDefaultBox
            detailsSx={{
              display: 'flex',
              flexDirection: ['column', null, 'row'],
              gap: 2,
            }}
            title="Description"
          >
            <Stack flex={1} gap={2}>
              <VailTextField
                control={form.control}
                disabled={!!componentID}
                label="Name"
                name="name"
                rules={{
                  validate: {
                    canUseName: async (data) =>
                      canUseName(orgID, componentID, data),
                  },
                }}
              />
              <WorkshopTitlePreview name={form.watch('name')} />
              <VailSelect
                disabled
                defaultValue={ComponentType.semanticSearch}
                label="Type"
                name="type"
                options={[
                  {
                    label: 'Semantic Search',
                    value: ComponentType.semanticSearch,
                  },
                ]}
              />
            </Stack>
            <Stack flex={1}>
              <VailTextField
                multiline
                control={form.control}
                label="Description"
                maxRows={5}
                minRows={5}
                name="description"
                rules={{ required: false }}
              />
            </Stack>
          </VailAccordion>
        </Grid>
        <Grid size={12}>
          <VailAccordion
            useDefaultBox
            detailsSx={{
              display: 'flex',
              flexDirection: ['column', null, 'row'],
              gap: 2,
            }}
            title="LLM Settings"
          >
            <Grid
              container
              alignItems="center"
              justifyContent="center"
              spacing={2}
            >
              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="Only matches above this similarity threshold are suggested. Otherwise, the question is considered not in our database.">
                  <Box>
                    <VailNumberInput
                      asFloat
                      control={form.control}
                      label="Go Straight to Answer Threshold"
                      name="settings.largeLanguageModelSettings.thresholdToGoStraightToAnswer"
                      rules={{
                        max: { message: 'The maximum value is 1', value: 1 },
                        min: { message: 'The minimum value is 0', value: 0 },
                        required: {
                          message: 'This field is required',
                          value: true,
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid>

              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="If the first match's similarity is above this threshold, we answer immediately without suggesting similar questions.">
                  <Box>
                    <VailNumberInput
                      asFloat
                      label="Add Question To Suggestion Threshold"
                      name="settings.largeLanguageModelSettings.thresholdToAddQuestionToSuggestions"
                      rules={{
                        max: { message: 'The maximum value is 1', value: 1 },
                        min: { message: 'The minimum value is 0', value: 0 },
                        required: {
                          message: 'This field is required',
                          value: true,
                        },
                      }}
                    />
                  </Box>
                </Tooltip>
              </Grid>

              <Tooltip title="The namespace used for vector embedding queries.">
                <Grid
                  size={{
                    md: 6,
                    xs: 12,
                  }}
                >
                  <TextField
                    fullWidth
                    label="Vector Embeddings Namespace"
                    value={form.watch(
                      'settings.largeLanguageModelSettings.pineconeNamespace'
                    )}
                  />
                </Grid>
              </Tooltip>

              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="If true we will check the user input for adverse events">
                  <Box>
                    <VailSwitch
                      control={form.control}
                      label="Adverse event check"
                      name="settings.largeLanguageModelSettings.adverseEventCheck"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Tooltip title="Recipe step to return to when the user no longer wants to ask questions.">
                <Grid
                  size={{
                    md: 6,
                    xs: 12,
                  }}
                >
                  <TextField
                    disabled
                    fullWidth
                    id={safeCypressID('Semantic search outro step')}
                    label="Semantic Search Outro Step"
                    value={`workshop/${org.data?.slug}/${
                      camelize(form.watch('name')) ?? ''
                    }/conversationHandler/OffRamp`}
                  />
                </Grid>
              </Tooltip>

              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <When
                  condition={form.watch(
                    'settings.largeLanguageModelSettings.adverseEventCheck'
                  )}
                >
                  <Stack spacing={2}>
                    <Tooltip title="Recipe to follow when an adverse event occurs.">
                      <Box>
                        <VailSelect
                          control={form.control}
                          label="Adverse Event Check"
                          name="settings.largeLanguageModelSettings.adverseEventDetectedStepRecipe"
                          options={recipes.data?.map((recipe) => ({
                            label: recipe.name,
                            value: recipe.id,
                          }))}
                        />
                      </Box>
                    </Tooltip>

                    <Tooltip title="Version of the recipe used for the adverse event check.">
                      <Box>
                        <VailSelect
                          control={form.control}
                          label="Version"
                          name="settings.largeLanguageModelSettings.adverseEventDetectedStep"
                          options={adverseEventCheckHistory.data?.map(
                            (history) => ({
                              label: history.name,
                              value: history.id,
                            })
                          )}
                        />
                      </Box>
                    </Tooltip>
                  </Stack>
                </When>
              </Grid>
              <Grid size={12}>
                <Tooltip
                  sx={{ display: 'inline-block' }}
                  title="Use custom prompt for the OpenAI Adverse Event Turbo model."
                >
                  <Box>
                    <VailSwitch
                      control={form.control}
                      label="Custom OpenAi Adverse Event Prompt"
                      name="settings.largeLanguageModelSettings.customOpenAiAdverseEventTurboPrompt"
                      onChange={async (checked) => {
                        if (checked) return

                        form.setValue(
                          'settings.largeLanguageModelSettings.openAiAdverseEventTurboPrompt',
                          defaultSemanticSearch.largeLanguageModelSettings
                            .openAiAdverseEventTurboPrompt
                        )
                      }}
                    />
                  </Box>
                </Tooltip>

                {openAiAdverseEventTurboPrompt.fields.map((field, idx) => (
                  <Stack
                    key={field.id}
                    gap={2}
                    my={3}
                    sx={{
                      display: withCustomPrompt ? 'flex' : 'none',
                    }}
                  >
                    <Button
                      sx={{ ml: 'auto', width: 'max-content' }}
                      onClick={() => openAiAdverseEventTurboPrompt.remove(idx)}
                    >
                      Remove content&role
                    </Button>
                    <Tooltip title="Prompt to use for GPT-3.5-Turbo's adverse event detection.">
                      <Box>
                        <VailTextField
                          multiline
                          control={form.control}
                          label="Content"
                          name={`settings.largeLanguageModelSettings.openAiAdverseEventTurboPrompt.${idx}.content`}
                          rules={{ minLength: 1, required: true }}
                        />
                      </Box>
                    </Tooltip>
                    <Tooltip title="Role used by GPT-3.5-Turbo for adverse event detection.">
                      <Box>
                        <VailSelect
                          control={form.control}
                          label="role"
                          name={`settings.largeLanguageModelSettings.openAiAdverseEventTurboPrompt.${idx}.role`}
                          options={[
                            { label: 'Assistant', value: 'assistant' },
                            { label: 'User', value: 'user' },
                          ]}
                        />
                      </Box>
                    </Tooltip>
                  </Stack>
                ))}
                <Button
                  sx={{
                    display: withCustomPrompt ? 'block' : 'none',
                  }}
                  onClick={() =>
                    openAiAdverseEventTurboPrompt.append({
                      content: '',
                      role: 'assistant',
                    })
                  }
                >
                  Add Prompt
                </Button>
              </Grid>
            </Grid>
          </VailAccordion>
        </Grid>
        <Grid alignItems="flex-start" size={12}>
          <VailAccordion title="Search Prompt">
            <VailSwitch
              control={form.control}
              label="Include default questions"
              name="settings.largeLanguageModelSettings.includeDefaultQuestions"
              onChange={(checked) => {
                form.setValue(
                  'settings.largeLanguageModelSettings.defaultQuestionKeys',
                  tempDefaultQuestion,
                  { shouldDirty: true, shouldValidate: true }
                )
                const currentValue = form.getValues(
                  'settings.largeLanguageModelSettings.defaultQuestionKeys'
                )
                setDefaultTempValue(currentValue.filter(Boolean))
                if (checked) return

                form.setValue(
                  'settings.largeLanguageModelSettings.defaultQuestionKeys',
                  ['']
                )
              }}
            />
            <When
              condition={form.watch(
                'settings.largeLanguageModelSettings.includeDefaultQuestions'
              )}
            >
              <Tooltip title="Text displayed when default questions are configured in the FAQ data.">
                <Box>
                  <VailTextField
                    multiline
                    control={form.control}
                    defaultValue="Default Question"
                    helperText="Each line is a new text bubble."
                    label="Default Question Text"
                    minRows={5}
                    name="settings.largeLanguageModelSettings.defaultQuestionText"
                  />
                </Box>
              </Tooltip>

              <If condition={!!current.data?.settings?.questions}>
                <Then>
                  <Tooltip title="Keys of the default questions.">
                    <Box>
                      <VailSelect
                        control={form.control}
                        label="Default Question Keys"
                        name="settings.largeLanguageModelSettings.defaultQuestionKeys"
                        options={current.data?.settings?.questions?.map(
                          (q) => ({
                            label: q.question,
                            value: q.key,
                          })
                        )}
                        rules={{
                          minLength: {
                            message: 'The minimum length is 1 question',
                            value: 1,
                          },
                        }}
                        SelectProps={{ multiple: true }}
                      />
                    </Box>
                  </Tooltip>
                </Then>
                <Else>
                  <Typography
                    color="warning.main"
                    id={safeCypressID('no questions')}
                    variant="body2"
                  >
                    Warning: There are no questions yet to select from. Please
                    return to this tab after adding on or more questions to
                    select the default(s).”
                  </Typography>
                </Else>
              </If>
            </When>
            <Tooltip title="Text for the initial search prompt.">
              <Box>
                <VailTextField
                  control={form.control}
                  label="Initial search text"
                  name="settings.largeLanguageModelSettings.initialSearchText"
                />
              </Box>
            </Tooltip>

            <Tooltip title="Instructions for the semantic search component.">
              <Box>
                <FieldArray
                  control={form.control}
                  label="Search Instructions"
                  name="settings.largeLanguageModelSettings.searchInstructions"
                />
              </Box>
            </Tooltip>

            <Tooltip title="We only use the initial search text once; we use this prompt for all subsequent searches.">
              <Box>
                <FieldArray
                  control={form.control}
                  helperText="Note: If there is more than one “Returning Search Text” option defined, one option will be selected at random each time the user returns to the search."
                  label="Returning Search Text"
                  name="settings.largeLanguageModelSettings.returningSearchText"
                />
              </Box>
            </Tooltip>
          </VailAccordion>
        </Grid>
        <Grid alignItems="flex-start" size={12}>
          <VailAccordion title="Matching Questions found">
            <Tooltip title="If true, display a message with the answer source.">
              <Box>
                <VailSwitch
                  control={form.control}
                  label="Show Sources"
                  name="settings.largeLanguageModelSettings.showSources"
                />
              </Box>
            </Tooltip>
          </VailAccordion>
        </Grid>
        <Grid alignItems="flex-start" size={12}>
          <VailAccordion title="Similar Question found">
            <Grid container spacing={2}>
              <Grid
                size={12}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Typography>Buttons</Typography>
                {buttonsQuestionsArray.fields.map((field, idx) => (
                  <Stack
                    key={field.id}
                    alignItems="center"
                    direction={{ md: 'row', xs: 'column' }}
                    spacing={2}
                  >
                    <Tooltip title="Text displayed on the created button.">
                      <Box sx={{ flex: 1 }}>
                        <VailTextField
                          control={form.control}
                          label="Text"
                          name={`settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.label`}
                        />
                      </Box>
                    </Tooltip>

                    <If condition={!!field.disabled}>
                      <Then>
                        <Tooltip title="Recipe to follow when the user clicks the button.">
                          <Box sx={{ flex: 1 }}>
                            <TextField
                              disabled
                              fullWidth
                              id={safeCypressID(
                                `settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.action`
                              )}
                              label="Action"
                              value={parseWorkshopActionButtons(
                                form.watch(
                                  `settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.action`
                                ) ?? '',
                                {
                                  org: org.data?.slug,
                                  solutionName: camelize(form.watch('name')),
                                }
                              )}
                            />
                          </Box>
                        </Tooltip>
                      </Then>
                      <Else>
                        <Tooltip title="Recipe to follow when the user clicks the button.">
                          <Box sx={{ flex: 1 }}>
                            <VailTextField
                              disabled
                              control={form.control}
                              label="Action"
                              name={`settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.action`}
                              rules={{ required: false }}
                            />
                          </Box>
                        </Tooltip>
                      </Else>
                    </If>
                    <Unless condition={!!field.disabled}>
                      <Tooltip title="URL to open when the button is clicked.">
                        <Box sx={{ flex: 1 }}>
                          <VailTextField
                            control={form.control}
                            label="url"
                            name={`settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.url`}
                            rules={{ required: false }}
                          />
                        </Box>
                      </Tooltip>

                      <Tooltip title="Button value to store internally.">
                        <Box sx={{ flex: 1 }}>
                          <VailTextField
                            control={form.control}
                            label="Value"
                            name={`settings.largeLanguageModelSettings.similarQuestionsFoundButtons.${idx}.value`}
                            rules={{ required: false }}
                          />
                        </Box>
                      </Tooltip>
                    </Unless>
                    <IconButton
                      aria-label="delete"
                      onClick={() => buttonsQuestionsArray.remove(idx)}
                    >
                      <Icon name="delete" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  id="add-button-similar-question"
                  startIcon={<Icon name="add" />}
                  sx={{ alignSelf: 'flex-start' }}
                  variant="outlined"
                  onClick={() =>
                    buttonsQuestionsArray.append({
                      action:
                        'components/semanticSearch/version2_0_0/semanticSearchHandler/askAnotherQuestion',
                      label: '',
                      url: '',
                      value: '',
                    })
                  }
                >
                  Button
                </Button>
              </Grid>
              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="Number of similar questions shown as buttons for keyword inputs.">
                  <Box>
                    <VailNumberInput
                      label="Similar Question Quantity"
                      name="settings.largeLanguageModelSettings.similarQuestionsQuantity"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="Number of similar questions to display as buttons when the user input is a keyword.">
                  <Box>
                    <VailNumberInput
                      label="Keyword Similar Question Quantity"
                      name="settings.largeLanguageModelSettings.keywordSimilarQuestionsQuantity"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              >
                <Tooltip title="Number of related questions shown as buttons.">
                  <Box>
                    <VailNumberInput
                      label="Related Question Quantity"
                      name="settings.largeLanguageModelSettings.relatedQuestionsQuantity"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                size={{
                  md: 6,
                  xs: 12,
                }}
              />
              <Grid size={12}>
                <Tooltip title="Text list used for the similar question prompt for keyword inputs; only one is shown at random.">
                  <Box>
                    <FieldArray
                      appendLabel="Similar Questions Text"
                      control={form.control}
                      helperText="Note: If there is more than one “Similar Questions Text” option defined, one option will be selected at random each the user enters a keyword that has 1 or more similar questions in the data base."
                      label="Similar Questions - Keyword Search"
                      name="settings.largeLanguageModelSettings.keywordSimilarQuestionsFoundText"
                      textFieldLabel="Similar Questions Text (Keyword Search)"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid size={12}>
                <Tooltip title="Text list for the similar question prompt; only one is displayed at a time, chosen at random.">
                  <Box>
                    <FieldArray
                      appendLabel="Similar Questions Text"
                      control={form.control}
                      helperText="Note: If there is more than one “Similar Questions Text” option defined, one option will be selected at random each the user enters a question that has 1 or more similar questions in the data base."
                      label="Similar Questions Text (Question Search)"
                      name="settings.largeLanguageModelSettings.similarQuestionsFoundText"
                      textFieldLabel="Similar Questions"
                    />
                  </Box>
                </Tooltip>
              </Grid>
            </Grid>
          </VailAccordion>
        </Grid>
        <Grid alignItems="flex-start" size={12}>
          <VailAccordion title="Question Not Found">
            <Grid container spacing={2}>
              <Grid size={12}>
                <Tooltip title="Text list for the question-not-found prompt; only one is displayed at a time, chosen at random.">
                  <Box>
                    <FieldArray
                      appendLabel="Question Not Found Text"
                      control={form.control}
                      helperText="Note: If there is more than one “Question Not Found Text” option defined, one option will be selected at random each the user enters a question that has no similar questions in the data base."
                      label="Question Not Found Text (Question Search)"
                      name="settings.largeLanguageModelSettings.questionNotFoundText"
                      textFieldLabel="Question Not Found"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid size={12}>
                <Tooltip title="Text lists for the question-not-found prompt when input is a keyword, with one displayed at a time, chosen at random.">
                  <Box>
                    <FieldArray
                      appendLabel="Question Not Found Text"
                      control={form.control}
                      helperText="Note: If there is more than one “Question Not Found Text” option defined, one option will be selected at random each the user enters a keyword that has no similar questions in the data base."
                      label="Question Not Found Text (Keyword Search)"
                      name="settings.largeLanguageModelSettings.keywordQuestionNotFoundText"
                      textFieldLabel="Question Not Found"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <Grid
                size={12}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
              >
                <Tooltip title="Buttons displayed when no similar questions are found.">
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography>Buttons</Typography>
                    {buttonsFieldArray.fields.map((field, idx) => {
                      const nextStep = parseWorkshopActionButtons(
                        form.watch(
                          `settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.action`
                        ) ?? '',
                        {
                          org: org.data?.slug,
                          solutionName: camelize(form.watch('name')),
                        }
                      )

                      return (
                        <Stack
                          key={field.id}
                          alignItems="center"
                          direction={{ md: 'row', xs: 'column' }}
                          spacing={2}
                        >
                          <VailTextField
                            control={form.control}
                            label="Text"
                            name={`settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.label`}
                            rules={{ required: false }}
                          />
                          <When condition={!!field.disabled}>
                            <TextField
                              disabled
                              fullWidth
                              id={safeCypressID(
                                `settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.action`
                              )}
                              label="Next Step"
                              value={nextStep}
                            />
                          </When>
                          <Unless condition={!!field.disabled}>
                            <Stack>
                              <TextField
                                fullWidth
                                select
                                id={safeCypressID(
                                  `questionNotFoundButton.${idx}.type`
                                )}
                                label="Type"
                                value={getButtonType(
                                  form.watch(
                                    `settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.url`
                                  )
                                )}
                                onChange={async (e) => {
                                  let value: 'tel:' | 'mailto:' | '' = ''
                                  switch (e.target.value) {
                                    case 'phone number':
                                      value = 'tel:'
                                      break
                                    case 'email':
                                      value = 'mailto:'
                                      break
                                    default:
                                      value = ''
                                      break
                                  }
                                  try {
                                    const url = form.watch(
                                      `settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.url`
                                    )
                                    if (
                                      url?.match(/^(tel:\d+)|(mailto:\S+)$/gm)
                                    )
                                      await confirm({
                                        description: `This will change the url of the button from ${url} to ${value}`,
                                        title: 'Are you sure?',
                                      })
                                    form.setValue(
                                      `settings.largeLanguageModelSettings.questionNotFoundButtons.${idx}.url`,
                                      value
                                    )
                                  } catch (e) {
                                    console.log('cancelled confirm', e)
                                  }
                                }}
                              >
                                {['phone number', 'email', 'url'].map((opt) => (
                                  <MenuItem
                                    key={opt}
                                    id={safeCypressID(`option ${opt}`)}
                                    value={opt}
                                  >
                                    {capitalize(opt)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Stack>

                            <TextField
                              disabled
                              fullWidth
                              label="Next Step"
                              value={parseWorkshopActionButtons(
                                form.watch(
                                  `settings.largeLanguageModelSettings.questionNotFoundButtons.0.action`
                                ),
                                {
                                  org: org.data?.slug,
                                  solutionName: camelize(form.watch('name')),
                                }
                              )}
                            />
                          </Unless>
                          <IconButton
                            aria-label="delete"
                            onClick={() => buttonsFieldArray.remove(idx)}
                          >
                            <Icon name="delete" />
                          </IconButton>
                        </Stack>
                      )
                    })}
                  </Box>
                </Tooltip>

                <Button
                  id="add-button-similar-question"
                  startIcon={<Icon name="add" />}
                  sx={{ alignSelf: 'flex-start' }}
                  variant="outlined"
                  onClick={() =>
                    buttonsFieldArray.append({
                      action: '',
                      label: '',
                      url: '',
                    })
                  }
                >
                  Button
                </Button>
              </Grid>
              <Grid size={12}>
                <Tooltip title="Flag to enable or disable question submission for unlisted questions.">
                  <Box>
                    <VailSwitch
                      control={form.control}
                      label="Allow Question Submission"
                      name="settings.largeLanguageModelSettings.submitQuestionEnabled"
                    />
                  </Box>
                </Tooltip>
              </Grid>
              <When
                condition={
                  !!form.watch(
                    'settings.largeLanguageModelSettings.submitQuestionEnabled'
                  )
                }
              >
                <Grid
                  size={{
                    md: 6,
                    xs: 12,
                  }}
                >
                  <VailTextField
                    control={form.control}
                    defaultValue="Submit my question"
                    label="Question Submission Button Text"
                    name="settings.largeLanguageModelSettings.submitQuestionButtonText"
                  />
                </Grid>
                <Grid
                  size={{
                    md: 6,
                    xs: 12,
                  }}
                >
                  <VailTextField
                    control={form.control}
                    label="Email Address for Submission "
                    name="settings.largeLanguageModelSettings.submitQuestionCustomerEmail"
                    type="email"
                  />
                </Grid>
                <Grid size={12}>
                  <VailTextField
                    control={form.control}
                    defaultValue="Question Submission"
                    label="Submit Question Text"
                    name="settings.largeLanguageModelSettings.submitQuestionText"
                  />
                </Grid>
                <Grid size={12}>
                  <VailTextField
                    multiline
                    control={form.control}
                    helperText="Please use valid HTML"
                    label="Email Body"
                    minRows={10}
                    name="settings.largeLanguageModelSettings.submitQuestionExtraBody"
                    rules={{
                      validate: {
                        isValidHTML: (value) => {
                          if (typeof value !== 'string') return true
                          const doc = document.createElement('div')
                          doc.innerHTML = value
                          const returnValue =
                            doc.innerHTML === value ||
                            "The email body isn't valid HTML"
                          doc.remove()
                          return returnValue
                        },
                      },
                    }}
                  />
                </Grid>
              </When>
            </Grid>
          </VailAccordion>
        </Grid>

        <Grid
          size={12}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 'auto',
            pb: 2,
          }}
        ></Grid>
      </Grid>
    </FormProvider>
  )
}

function getButtonType(value?: string) {
  if (!value) return 'url'
  if (value.includes('tel:')) return 'phone number'
  if (value.includes('mailto:')) return 'email'
  return 'url'
}

function parseWorkshopActionButtons(
  action?: string | null,
  extraInfo?: {
    org?: string
    solutionName?: string
  }
) {
  if (!action || !extraInfo) return ''
  const { org, solutionName } = extraInfo
  if (!org && action.includes('org')) return 'Waiting for org'
  if (!solutionName && action.includes('solutionName'))
    return 'Please update the name of the semantic search'
  return action
    .replace(/<org>/gm, org ?? '')
    .replace(/<solutionName>/gm, solutionName ?? '')
    .split('/')
    .map(camelize)
    .join('/')
}
